import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { createSlug } from '../../../util/urlHelpers';
import { getImprovedLocationText } from '../../../util/misc';
import { hasContent } from '../../../util/memberArea';
import { NamedLink, FavoriteListingButton, ShareListingButton } from '../../../components';
import { assetClassOf, sideLabelOf } from '../assetClasses';

import css from './ListingRows.module.css';

/**
 * The listing rows (member area N3; ACs "Listing row component", Member Home
 * design). Built once and used wherever listings are listed: today's search
 * page, My Hub and Favourites (and later Home, Deals and My listings).
 *
 *   Desktop: a table, as the design's Deals table. Colour bar (asset class,
 *            full height) · Listing (full title: it wraps, never cut, ACs) ·
 *            Type (For sale / Wanted) · Price / budget · Location · the asset
 *            class's own columns when every row is one class · share and save.
 *   Mobile:  the tag, the title, then price · location · asset class; share
 *            and save on the right. Also used on wider screens when the rows'
 *            own space is too narrow for the table (`tableMinWidth`).
 *
 * Share and save are always visible, as in the design (the ACs said "on
 * hover"; the user asked to match the design, 2026-10-21).
 *
 * Only listing fields that exist today are shown (decision D13). An empty list
 * shows nothing (the empty-block rule).
 */

/** "£18,500,000" (or "18,500,000 XYZ" for an unknown currency). Pure. */
export const formatAmount = (amount, currency) => {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return null;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: String(currency || 'GBP').toUpperCase(),
      maximumFractionDigits: 0,
    }).format(value);
  } catch (e) {
    return `${value.toLocaleString('en-GB')}${currency ? ` ${currency}` : ''}`;
  }
};

/** The price cell: the asking price, or "Up to" the budget for Wanted listings. Pure. */
export const priceTextOf = publicData => {
  const { listingType, currency, asking_price, Budget_Maximum, Budget_minimum, poa } =
    publicData || {};
  if (poa) return 'Price on application';
  if (listingType === 'search') {
    const max = formatAmount(Budget_Maximum, currency);
    if (max) return `Up to ${max}`;
    const min = formatAmount(Budget_minimum, currency);
    return min ? `From ${min}` : 'Undisclosed';
  }
  return formatAmount(asking_price, currency) || 'Undisclosed';
};

// The asset class's own columns: the listing fields limited to that class
// (for example the age of a car, the size of a yacht). Pure.
export const classColumnsFor = (classId, listingFields = []) =>
  (listingFields || []).filter(f => {
    const ids = f?.categoryConfig?.categoryIds;
    return (
      Array.isArray(ids) &&
      ids.length > 0 &&
      ids.includes(classId) &&
      ['enum', 'multi-enum', 'text', 'long'].includes(f.schemaType)
    );
  });

// What one field shows for a listing: option labels, without "n/a" answers. Pure.
export const fieldValueText = (field, publicData) => {
  const raw = publicData?.[field.key];
  if (raw === undefined || raw === null || raw === '') return null;
  const labelOf = option =>
    (field.enumOptions || []).find(o => String(o.option) === String(option))?.label || option;
  if (Array.isArray(raw)) {
    const shown = raw.filter(v => !['na', 'any', 'notApplicable'].includes(v)).map(labelOf);
    return shown.length ? shown.join(', ') : null;
  }
  if (['na', 'any', 'notApplicable'].includes(raw)) return null;
  return String(labelOf(raw));
};

export const LOCATION_MAX = 80;

/** The location, cut to 80 characters with "…" (user, 2026-10-21). Pure. */
export const shortLocation = (text, max = LOCATION_MAX) => {
  const value = String(text || '').trim();
  return value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;
};

const fieldLabel = field =>
  field?.showConfig?.label || field?.filterConfig?.label || field?.saveConfig?.label || field.key;

/** The narrowest width the table needs; below it the rows are stacked. Pure. */
export const tableMinWidth = extraColumns => 700 + extraColumns * 104;

const ListingRows = props => {
  const { listings, searchString, onListingClick, className } = props;
  const config = useConfiguration();
  const rootRef = useRef(null);
  const [compact, setCompact] = useState(false);

  const categories = config?.categoryConfiguration?.categories || [];
  const listingFields = config?.listing?.listingFields || [];

  // One asset class across every row: add its own columns.
  const safeListings = listings || [];
  const classes = [...new Set(safeListings.map(l => l?.attributes?.publicData?.categoryLevel1))];
  const singleClass = classes.length === 1 ? classes[0] : null;
  const extraFields = singleClass ? classColumnsFor(singleClass, listingFields) : [];
  const minWidth = tableMinWidth(extraFields.length);

  // Stacked rows whenever the space is too narrow for the table, not only on
  // phones (for example the search page beside the sidebar and the filters).
  const hasRows = hasContent(listings);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof window === 'undefined') return undefined;
    const measure = () => setCompact(el.clientWidth > 0 && el.clientWidth < minWidth);
    measure();
    if (typeof window.ResizeObserver === 'function') {
      const observer = new window.ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [minWidth, hasRows]);

  if (!hasRows) return null;

  // The design's columns (Member Home, Deals table).
  const columns = [
    '5px',
    'minmax(180px, 1.6fr)',
    '84px',
    '140px',
    'minmax(110px, 1fr)',
    ...extraFields.map(() => 'minmax(90px, 0.8fr)'),
    '80px',
  ].join(' ');

  return (
    <div ref={rootRef} className={classNames(css.root, { [css.compact]: compact }, className)}>
      {/* Column titles: visual only; each row reads as one link. */}
      <div className={css.headRow} style={{ gridTemplateColumns: columns }} aria-hidden="true">
        <span />
        <span>Listing</span>
        <span>Type</span>
        <span>Price / budget</span>
        <span>Location</span>
        {extraFields.map(f => (
          <span key={f.key}>{fieldLabel(f)}</span>
        ))}
        <span />
      </div>

      {listings.map(listing => {
        const id = listing.id.uuid;
        const { title = '', publicData = {} } = listing.attributes || {};
        const asset = assetClassOf(publicData.categoryLevel1, categories);
        const isWanted = publicData.listingType === 'search';
        const price = priceTextOf(publicData);
        const fullLocation = getImprovedLocationText(publicData);
        const location = shortLocation(fullLocation) || '—';
        const tag = (
          <span className={classNames(css.tag, { [css.tagWanted]: isWanted })}>
            {sideLabelOf(publicData.listingType)}
          </span>
        );

        return (
          <NamedLink
            key={id}
            name="ListingPage"
            params={{ id, slug: createSlug(title) }}
            to={{ state: { searchString } }}
            className={css.row}
            style={{ gridTemplateColumns: columns }}
            onClick={() => onListingClick && onListingClick(listing)}
          >
            {/* The colour bar runs the full height of the row (design). */}
            <span className={css.barSlot}>
              <span className={css.colourBar} style={{ background: asset.color }} title={asset.label} />
            </span>
            <span className={css.titleCell}>
              <span className={css.mobileOnly}>{tag}</span>
              <span className={css.title}>{title}</span>
              {/* Mobile: price (bold) · location · asset class. */}
              <span className={classNames(css.metaLine, css.mobileOnly)}>
                <span className={css.metaPrice}>{price}</span>
                {[location, asset.label].filter(Boolean).map(t => ` · ${t}`).join('')}
              </span>
            </span>
            <span className={css.desktopOnly}>{tag}</span>
            <span className={classNames(css.price, css.desktopOnly)}>{price}</span>
            <span className={classNames(css.cell, css.desktopOnly)} title={fullLocation || undefined}>
              {location}
            </span>
            {extraFields.map(f => (
              <span key={f.key} className={classNames(css.cell, css.desktopOnly)}>
                {fieldValueText(f, publicData) || '—'}
              </span>
            ))}
            {/* Share, then save: always visible (design). Each stops its own
                click, so it does not open the listing. */}
            <span className={css.actions}>
              <ShareListingButton listing={listing} className={css.iconButton} />
              <FavoriteListingButton listingId={id} className={css.iconButton} />
            </span>
          </NamedLink>
        );
      })}
    </div>
  );
};

export default ListingRows;
